'use client';

import { showToast } from '@finn/design-system/atoms/toast';
import { SpacingWrapper } from '@finn/platform-modules';
import { ModalKey, useOpenModal } from '@finn/ua-modals';
import { TrackComponentView } from '@finn/ua-tracking';
import { ReferralPromoCosmicMetadata } from '@finn/ui-cosmic';
import {
  config,
  parseToHtml,
  traceabilityHeadersBrowser,
  useSession,
} from '@finn/ui-utils';
import axios from 'axios';
import { useEffect } from 'react';
import useSWR from 'swr';

import { ReferralPromoDesktop } from './ReferralPromoDesktop';
import { ReferralPromoMobile } from './ReferralPromoMobile';

type Props = {
  data: ReferralPromoCosmicMetadata;
};

type ReferralPromoData = ReferralPromoCosmicMetadata & {
  inviteLink?: string;
  isLoading?: boolean;
  error?: any;
};

// a single-use simplified version of Promo
export const ReferralPromo = ({ data }: Props) => {
  const [session] = useSession();
  const openModal = useOpenModal();

  const fetcher = async (url: string) => {
    const response = await axios.get(url, {
      headers: traceabilityHeadersBrowser(),
    });
    const link = response.data.link;

    return link.replace(/einladen/g, 'invite');
  };

  const {
    data: inviteLink,
    error,
    isLoading,
  } = useSWR(
    session
      ? `${config.VOUCHER_API_URL}/referral_link/${session.user.email}`
      : null,
    fetcher,
    { errorRetryCount: 1 }
  );

  useEffect(() => {
    if (error) {
      showToast({
        // German error message for failed referral voucher retrieval
        title: data.voucher.failure_toast,
        status: 'error',
      });
    }
  }, [error, data.failure_toast]);

  useEffect(() => {
    if (session === null) {
      openModal(ModalKey.LOGIN);
    }
  }, [openModal, session]);

  return (
    <SpacingWrapper html_id={data.html_id} noPaddingTop>
      <TrackComponentView
        name="DynamicPromo"
        additionalProps={{ slug: 'referral-promo-cp' }}
      >
        <div id={data.id} data-testid="promo-cp">
          <h2 className="mobile-t3-semibold md:web-t3-semibold">
            {parseToHtml(data.title)}
          </h2>
          <div className="py-10 body-16-light [&>p]:py-1 [&>p]:body-16-light">
            {parseToHtml(data.description)}
          </div>
          <div className="hidden md:block">
            <ReferralPromoDesktop
              data={
                { ...data, inviteLink, isLoading, error } as ReferralPromoData
              }
            />
          </div>
          <div className="block md:hidden">
            <ReferralPromoMobile
              data={
                { ...data, inviteLink, isLoading, error } as ReferralPromoData
              }
            />
          </div>
        </div>
      </TrackComponentView>
    </SpacingWrapper>
  );
};
